import React from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import { handleMutationError } from '../utils/errors'

export type OnboardingType =
	| 'PARTNER_CONTRACT'
	| 'DEVICE_CONTRACT'
	| 'CHECKLIST'
	| 'MENU_STICKER'

const GET_PDF_QUERY = gql`
	query GetPdf(
		$restaurantId: ID
		$documents: [OnboardingType!]!
		$deviceDescription: String
		$duplicate: Boolean!
	) {
		getPdf(
			restaurantId: $restaurantId
			documents: $documents
			deviceDescription: $deviceDescription
			duplicate: $duplicate
		)
	}
`

const saveBase64Pdf = (base64: string, fileName: string) => {
	const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0))
	const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
	const link = document.createElement('a')
	link.href = url
	link.download = fileName
	link.click()
	URL.revokeObjectURL(url)
}

// Lädt die gewählten Dokumente des Onboardings als ein PDF zum Ausdrucken herunter, ohne restaurantId
// blanko. Erst auf Klick und ohne Cache.
export const usePdfDownload = () => {
	const [loadPdf, { loading }] = useLazyQuery(GET_PDF_QUERY, { fetchPolicy: 'no-cache' })

	const downloadPdf = React.useCallback(
		async ({
			restaurantId,
			documents,
			deviceDescription,
			duplicate,
			fileName,
		}: {
			restaurantId: string | null
			documents: OnboardingType[]
			deviceDescription: string | null
			duplicate: boolean
			fileName: string
		}) => {
			const { data, error } = await loadPdf({
				variables: { restaurantId, documents, deviceDescription, duplicate },
			})
			if (!data?.getPdf) {
				handleMutationError('getPdf', error?.graphQLErrors)
				return
			}
			saveBase64Pdf(data.getPdf as string, fileName)
		},
		[loadPdf]
	)

	return React.useMemo(() => ({ downloadPdf, loading }), [downloadPdf, loading])
}
